import Blink from '@components/Blink'
import SvgIcon from '@components/SvgIcon'
import { FunctionComponent, useState } from 'react'
import styled, { css, useTheme } from 'styled-components'
import {
  dayjs,
  formatByLocale,
  isSameDate,
  getFormattedDateWebarchive,
} from '@utils/date'
import { Date } from '@cook-types/cook'
import Head from 'next/head'
import config from '@config'

const {
  backend: { baseUrl },
} = config

interface NavigationProps {
  date: Date
}

const StyledNavigation = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => css`
    display: flex;
    color: ${brand_55};
    font-family: ${goodOT};
    font-weight: 600;
    font-size: 16px;
    margin: 10px 0;
  `}
`

const StyledBlink = styled(Blink)`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
  }) => css`
    background: ${brand_55};
    outline: 0;
    cursor: pointer;
    height: 30px;
    width: 30px;
    font-size: 30px;
    padding: 0;
  `}
`

const DisabledLink = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_20 },
      },
    },
  }) => css`
    background-color: ${grey_20};
    height: 30px;
    width: 30px;
  `}
`

const StyledDate = styled.span`
  margin-left: 12px;
  line-height: 30px;
`

interface Dates {
  archiveDate: string
  prevDate: string
  nextDate: string | null
}

const getDates = (date: Date) => {
  const { value, format } = date
  const dateObject = dayjs(value, format)
  const prevDate = dateObject.subtract(1, 'day')
  const nextDate = isSameDate(dayjs(), value, 'day')
    ? null
    : dateObject.add(1, 'day')
  return {
    archiveDate: formatByLocale(dateObject, 'dddd, D, MMMM YYYY'),
    prevDate: getFormattedDateWebarchive(prevDate),
    nextDate: !nextDate ? null : getFormattedDateWebarchive(nextDate),
  }
}

const Navigation: FunctionComponent<NavigationProps> = ({ date }) => {
  const theme = useTheme()
  const [dates] = useState<Dates>(() => getDates(date))

  const { archiveDate, prevDate, nextDate } = dates
  return (
    <StyledNavigation>
      <Head>
        {nextDate ? (
          <>
            <link
              rel="prev"
              href={`${baseUrl}/services/webarchiv/${prevDate}/`}
            />
            <link
              rel="next"
              href={`${baseUrl}/services/webarchiv/${nextDate}/`}
            />
          </>
        ) : (
          <link rel="prev" href={`${baseUrl}/webarchive/${prevDate}`} />
        )}
      </Head>
      <StyledBlink
        href={`/services/webarchiv/${prevDate}/`}
        target="_self"
        rel="prev">
        <SvgIcon
          iconName="gallery-previous"
          size={30}
          color={theme.colors.white}
        />
      </StyledBlink>
      {nextDate ? (
        <StyledBlink
          href={`/services/webarchiv/${nextDate}/`}
          target="_self"
          rel="next">
          <SvgIcon
            iconName="gallery-next"
            size={30}
            color={theme.colors.white}
          />
        </StyledBlink>
      ) : (
        <DisabledLink>
          <SvgIcon
            iconName="gallery-next"
            size={30}
            color={theme.colors.white}
          />
        </DisabledLink>
      )}
      <StyledDate>{archiveDate}</StyledDate>
    </StyledNavigation>
  )
}

export default Navigation
